import {useTranslation} from '@aboutyou/i18n';
import {useTrackWishlist} from '@aboutyou/tracking';
import {useShowToast} from '@aboutyou/ui-components';
import {
  useAddProductToWishlistMutation,
  useFullSession,
  useGetProductIsInWishlist,
  useWishlistCount,
} from '@aboutyou/user';
import {useCallback, useEffect, useRef} from 'react';
import {useSizesSection} from '../../ArticleDetail/ProductInformationBox/MiniArticleDetail/useGetMinimalProductBulkSection';
import {useGetBadgeListKeys} from '../../ArticleDetail/utils/useGetBadgeListKeys';
import {useSubmitAddToWishlistMission} from '../../Coins/utils/useSubmitAddToWishlistMission';

/**
 * Minimal hook for quick view: adds product to wishlist once on mount
 *  */
export const useQuickViewAutoAddToWishlist = (productId: number) => {
  const getProductIsInWishlist = useGetProductIsInWishlist();
  const showToast = useShowToast();
  const fullSession = useFullSession();
  const wishlistCount = useWishlistCount();
  const addedToWishlistRef = useRef(false);
  const {t} = useTranslation();

  const {badgesListKeys} = useGetBadgeListKeys({
    isInMiniADPDialog: false,
    productId,
  });

  const sizesSection = useSizesSection(productId);
  const trackWishlist = useTrackWishlist({
    streamPosition: 0,
    product: (sizesSection.data || {}).product!,
    price: sizesSection.data?.price,
    brand: (sizesSection.data || {}).brand!,
    badges: badgesListKeys,
  });

  const onError = useCallback(
    (err?: string) => {
      showToast({
        content: err || t('Oops! Something went wrong!'),
        type: 'ERROR',
      });
    },
    [showToast, t],
  );

  const onAddToWishlistSuccess = useSubmitAddToWishlistMission();

  const addProductToWishlist = useAddProductToWishlistMutation(() => {});

  const addToWishlist = useCallback(async () => {
    if (!fullSession || wishlistCount.isLoading || addedToWishlistRef.current) {
      return;
    }

    if (getProductIsInWishlist({productId})) {
      showToast({
        content: t('This product is already in your wishlist.'),
        type: 'WARNING',
      });
      return;
    }

    try {
      addedToWishlistRef.current = true;

      await addProductToWishlist.mutateAsync({
        productId,
        onError,
        onSuccess: onAddToWishlistSuccess,
      });

      trackWishlist('add-to-wishlist');
      showToast({
        content: t('The item has been added to your wishlist'),
        type: 'SUCCESS',
      });
    } catch (e) {
      onError();
    }
  }, [
    productId,
    fullSession,
    wishlistCount.isLoading,
    getProductIsInWishlist,
    addProductToWishlist,
    trackWishlist,
    showToast,
    onError,
    t,
    onAddToWishlistSuccess,
  ]);

  useEffect(() => {
    addToWishlist();
  }, [addToWishlist]);
};
